import { notification } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { extractList } from 'pages/star-shared/utils';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { formatData } from 'utils/common';
import { useDataIssues } from './context';
import { PAGE_SIZE, SEARCH_DEBOUNCE_MS, issuesErrorMessage } from './utils';

export interface IssueList<T> {
  items: T[];
  total: number;
  page: number;
  setPage: (page: number) => void;
  /** Chữ đang có trong ô tìm. */
  input: string;
  setInput: (value: string) => void;
  /** Tìm ngay, không đợi hết thời gian chờ (bấm Enter). */
  searchNow: () => void;
  /** Từ khoá của các dòng đang hiện. */
  keyword: string;
  loading: boolean;
  /** Lần tải gần nhất bị lỗi. */
  failed: boolean;
}

/**
 * Tải một danh sách của trang Vấn đề dữ liệu: GET `${API.dataIssues}/${endpoint}` với
 * `page`, `limit`, `q`; máy chủ phân trang và tìm kiếm. Trả về `{ items, total, page, limit }`
 * (cùng dạng với GET /data-import/logs).
 */
export function useIssueList<T>(endpoint: string, errorTitle: string): IssueList<T> {
  const { refreshKey } = useDataIssues();
  const [page, setPage] = useState(1);
  const [input, setInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // Trang + từ khoá của các dòng đang hiện trong bảng.
  const loadedQuery = useRef('');

  const applyKeyword = useCallback((value: string) => {
    setKeyword(value.trim());
    setPage(1);
  }, []);

  // Đợi người dùng ngừng gõ rồi mới gửi từ khoá.
  useEffect(() => {
    if (input.trim() === keyword) return undefined;
    const timer = window.setTimeout(() => applyKeyword(input), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [input, keyword, applyKeyword]);

  const searchNow = useCallback(() => applyKeyword(input), [applyKeyword, input]);

  useEffect(() => {
    let cancelled = false;
    const query = `${page}|${keyword}`;
    setLoading(true);
    setFailed(false);
    AxiosUtils.get(
      `${API.dataIssues}/${endpoint}`,
      formatData({ page, limit: PAGE_SIZE, q: keyword })
    )
      .then((res: any) => {
        if (cancelled) return;
        const next = extractList<T>(res);
        // Danh sách ngắn lại (vừa nhập file mới) mà đang đứng ở trang đã hết dòng: về trang cuối.
        const lastPage = Math.max(1, Math.ceil(next.total / PAGE_SIZE));
        if (!next.items.length && page > lastPage) {
          setPage(lastPage);
          return;
        }
        loadedQuery.current = query;
        setItems(next.items);
        setTotal(next.total);
      })
      .catch((err) => {
        if (cancelled) return;
        // Bấm Tải lại mà lỗi: giữ các dòng đang hiện (vẫn đúng từ khoá). Đổi từ khoá / trang mà
        // lỗi: bỏ các dòng cũ vì chúng không khớp điều đang tìm.
        if (loadedQuery.current !== query) {
          loadedQuery.current = '';
          setItems([]);
          setTotal(0);
        }
        setFailed(true);
        notification.error({
          message: errorTitle,
          description: issuesErrorMessage(err),
          duration: 6,
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [endpoint, errorTitle, page, keyword, refreshKey]);

  return { items, total, page, setPage, input, setInput, searchNow, keyword, loading, failed };
}
